import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";

// Shared pricing grid used on Home and /pricing. Data comes from /api/pricing-packages.
export default function PricingCards({ packages, cardId = "pricing-card", ctaId = "pricing-start" }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:items-stretch">
      {packages.map((p, i) => {
        const slug = p.slug || p.name?.toLowerCase().replace(/\s+/g, "-");
        return (
          <motion.div key={slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: i * 0.07, duration: 0.5 }} className="flex">
            <PricingCard p={p} slug={slug} cardId={cardId} ctaId={ctaId} />
          </motion.div>
        );
      })}
    </div>
  );
}

function PricingCard({ p, slug, cardId, ctaId }) {
  const dark = p.highlight;
  const entry = !dark && p.badge;
  const shell = dark
    ? "bg-[#050505] text-white border-[#FF6A00] shadow-[0_30px_80px_-30px_rgba(255,106,0,0.55)] lg:-translate-y-3"
    : entry ? "bg-white border-[#FF6A00]/50 ring-1 ring-[#FF6A00]/20" : "bg-white border-black/5";
  const muted = dark ? "text-white/60" : "text-black/55";
  return (
    <div data-testid={`${cardId}-${slug}`}
      className={`relative w-full rounded-2xl p-7 flex flex-col border transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-2xl ${shell}`}>
      {p.badge && (
        <div data-testid={`${cardId}-badge-${slug}`}
          className={`inline-flex self-start text-[10px] font-mono uppercase tracking-[0.2em] px-2.5 py-1 rounded-full mb-4 ${
            dark ? "bg-[#FF6A00] text-black" : "bg-[#FF6A00]/10 text-[#C2410C] border border-[#FF6A00]/30"}`}>
          {p.badge}
        </div>
      )}
      <div className="font-display text-lg font-semibold">{p.name}</div>
      <div className="mt-3 flex items-baseline gap-2 flex-wrap">
        <span className={`font-display text-5xl font-bold tracking-tighter ${entry ? "text-[#FF6A00]" : ""}`} data-testid={`${cardId}-price-${slug}`}>{p.price}</span>
        {p.cadence && <span className={`text-sm ${muted}`}>{p.cadence}</span>}
      </div>
      {p.description && <div className={`mt-3 text-sm ${dark ? "text-white/70" : "text-black/60"}`}>{p.description}</div>}
      <ul className={`mt-6 pt-6 border-t space-y-2.5 text-sm flex-1 ${dark ? "border-white/10" : "border-black/5"}`}>
        {(p.features || []).map((f) => (
          <li key={f} className="flex gap-2">
            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${dark || entry ? "text-[#FF6A00]" : "text-[#0066FF]"}`} />
            <span className={dark ? "text-white/85" : "text-black/75"}>{f}</span>
          </li>
        ))}
      </ul>
      <Link to={p.cta_href || "/contact"} data-testid={`${ctaId}-${slug}`}
        className={`mt-7 justify-center ${dark || entry ? "btn-primary" : "btn-ghost-dark"}`}>
        {p.cta_label || "Get Started"} <ArrowUpRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
